/* Amazon-style gallery lightbox for the simple-pdp + pdp-bundle galleries.
 *
 * Works off the shared gallery hooks (`[data-simple-main-image]` + the
 * `[data-simple-thumb]` strip). Clicking the main image (or pressing Enter on
 * it) opens a fullscreen overlay with a thumbnail rail, prev/next, and
 * zoom + pan (wheel / drag on desktop, pinch + double-tap on mobile).
 * Self-contained singleton — no dependency on the theme's Svelte modal.
 */
(function () {
  if (window.__pdpLightboxReady) return;
  window.__pdpLightboxReady = true;

  /* Upgrade Shopify CDN urls to a larger width for crisp zoom; leave others as-is. */
  function hiRes(url) {
    if (!url) return url;
    if (/cdn\/shop|cdn\.shopify/.test(url)) {
      if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$11800');
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=1800';
    }
    return url;
  }

  /* ---- Build the overlay once ---- */
  var overlay = document.createElement('div');
  overlay.className = 'pdp-lb';
  overlay.hidden = true;
  overlay.innerHTML =
    '<button class="pdp-lb__close" type="button" aria-label="Close">&times;</button>' +
    '<button class="pdp-lb__nav pdp-lb__nav--prev" type="button" aria-label="Previous">' +
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button>' +
    '<button class="pdp-lb__nav pdp-lb__nav--next" type="button" aria-label="Next">' +
      '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg></button>' +
    '<div class="pdp-lb__stage" data-lb-stage><img class="pdp-lb__img" alt="" data-lb-img></div>' +
    '<div class="pdp-lb__rail" data-lb-rail></div>';
  document.body.appendChild(overlay);

  var stage = overlay.querySelector('[data-lb-stage]');
  var img = overlay.querySelector('[data-lb-img]');
  var rail = overlay.querySelector('[data-lb-rail]');
  var btnClose = overlay.querySelector('.pdp-lb__close');
  var btnPrev = overlay.querySelector('.pdp-lb__nav--prev');
  var btnNext = overlay.querySelector('.pdp-lb__nav--next');

  var items = [];        // [{src, alt}]
  var index = 0;
  var scale = 1, tx = 0, ty = 0;

  function applyTransform() {
    img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    img.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    stage.classList.toggle('is-zoomed', scale > 1);
  }

  function resetZoom() {
    scale = 1; tx = 0; ty = 0;
    applyTransform();
  }

  function show(i) {
    index = (i + items.length) % items.length;
    var it = items[index];
    resetZoom();
    img.src = hiRes(it.src);
    img.alt = it.alt || '';
    rail.querySelectorAll('.pdp-lb__thumb').forEach(function (t, ti) {
      t.classList.toggle('is-active', ti === index);
    });
    var active = rail.children[index];
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'center' });
    var multi = items.length > 1;
    btnPrev.hidden = !multi;
    btnNext.hidden = !multi;
    rail.hidden = !multi;
  }

  function open(list, startIndex) {
    items = list;
    rail.innerHTML = '';
    items.forEach(function (it, ti) {
      var t = document.createElement('button');
      t.type = 'button';
      t.className = 'pdp-lb__thumb';
      t.innerHTML = '<img src="' + it.src + '" alt="" loading="lazy">';
      t.addEventListener('click', function () { show(ti); });
      rail.appendChild(t);
    });
    overlay.hidden = false;
    document.body.classList.add('pdp-lb-open');
    show(startIndex || 0);
  }

  function close() {
    overlay.hidden = true;
    document.body.classList.remove('pdp-lb-open');
    resetZoom();
  }

  btnClose.addEventListener('click', close);
  btnPrev.addEventListener('click', function () { show(index - 1); });
  btnNext.addEventListener('click', function () { show(index + 1); });
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay || e.target === stage) close();
  });
  document.addEventListener('keydown', function (e) {
    if (overlay.hidden) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') show(index - 1);
    else if (e.key === 'ArrowRight') show(index + 1);
  });

  /* ---- Zoom + pan ---- */
  function zoomAt(clientX, clientY, nextScale) {
    var rect = stage.getBoundingClientRect();
    var cx = clientX - rect.left - rect.width / 2;
    var cy = clientY - rect.top - rect.height / 2;
    var ratio = nextScale / scale;
    tx = cx - (cx - tx) * ratio;
    ty = cy - (cy - ty) * ratio;
    scale = nextScale;
    clampPan();
    applyTransform();
  }

  function clampPan() {
    if (scale <= 1) { tx = 0; ty = 0; return; }
    var rect = stage.getBoundingClientRect();
    var maxX = (rect.width * (scale - 1)) / 2;
    var maxY = (rect.height * (scale - 1)) / 2;
    tx = Math.max(-maxX, Math.min(maxX, tx));
    ty = Math.max(-maxY, Math.min(maxY, ty));
  }

  stage.addEventListener('wheel', function (e) {
    e.preventDefault();
    var next = Math.min(4, Math.max(1, scale * (e.deltaY < 0 ? 1.2 : 0.83)));
    zoomAt(e.clientX, e.clientY, next);
  }, { passive: false });

  img.addEventListener('dblclick', function (e) {
    zoomAt(e.clientX, e.clientY, scale > 1 ? 1 : 2.5);
  });

  // drag to pan (mouse)
  var dragging = false, lastX = 0, lastY = 0;
  img.addEventListener('mousedown', function (e) {
    if (scale <= 1) return;
    dragging = true; lastX = e.clientX; lastY = e.clientY;
    img.style.cursor = 'grabbing';
    e.preventDefault();
  });
  window.addEventListener('mousemove', function (e) {
    if (!dragging) return;
    tx += e.clientX - lastX; ty += e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    clampPan(); applyTransform();
  });
  window.addEventListener('mouseup', function () {
    dragging = false;
    if (scale > 1) img.style.cursor = 'grab';
  });

  // touch: pinch zoom + one-finger pan + double-tap
  var pinchDist = 0, touchPan = false, lastTX = 0, lastTY = 0, lastTap = 0;
  stage.addEventListener('touchstart', function (e) {
    if (e.touches.length === 2) {
      pinchDist = dist(e.touches);
    } else if (e.touches.length === 1) {
      if (scale > 1) { touchPan = true; lastTX = e.touches[0].clientX; lastTY = e.touches[0].clientY; }
      var now = Date.now();
      if (now - lastTap < 300) {
        zoomAt(e.touches[0].clientX, e.touches[0].clientY, scale > 1 ? 1 : 2.5);
      }
      lastTap = now;
    }
  }, { passive: true });
  stage.addEventListener('touchmove', function (e) {
    if (e.touches.length === 2) {
      var d = dist(e.touches);
      var mid = midpoint(e.touches);
      var next = Math.min(4, Math.max(1, scale * (d / (pinchDist || d))));
      pinchDist = d;
      zoomAt(mid.x, mid.y, next);
      e.preventDefault();
    } else if (touchPan && e.touches.length === 1) {
      tx += e.touches[0].clientX - lastTX; ty += e.touches[0].clientY - lastTY;
      lastTX = e.touches[0].clientX; lastTY = e.touches[0].clientY;
      clampPan(); applyTransform();
      e.preventDefault();
    }
  }, { passive: false });
  stage.addEventListener('touchend', function (e) {
    if (e.touches.length === 0) { touchPan = false; pinchDist = 0; }
  });
  function dist(t) { var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY; return Math.hypot(dx, dy); }
  function midpoint(t) { return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 }; }

  /* ---- Bind to galleries ---- */
  function collect(scope) {
    var thumbs = Array.prototype.slice.call(scope.querySelectorAll('[data-simple-thumb]'))
      .filter(function (t) { return !t.hidden; });
    return thumbs.map(function (t) {
      return { src: t.getAttribute('data-src'), alt: t.getAttribute('data-alt') || '', el: t };
    }).filter(function (x) { return x.src; });
  }

  function bind() {
    document.querySelectorAll('[data-simple-main-image]').forEach(function (main) {
      if (main.dataset.lbBound) return;
      main.dataset.lbBound = 'true';
      var scope = main.closest('.simple-pdp__gallery, .pdp-bundle__gallery, [data-simple-pdp], [data-pdp-bundle]') || document;
      var openHere = function () {
        var list = collect(scope);
        if (!list.length) list = [{ src: main.src, alt: main.alt }];
        var start = 0;
        for (var i = 0; i < list.length; i++) {
          if (list[i].src === main.getAttribute('src') || (list[i].el && list[i].el.classList.contains('is-active'))) { start = i; break; }
        }
        open(list.map(function (x) { return { src: x.src, alt: x.alt }; }), start);
      };
      main.style.cursor = 'zoom-in';
      main.addEventListener('click', openHere);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  // galleries can hydrate after load (dev server / theme editor)
  setTimeout(bind, 800);
})();
